import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, tap } from 'rxjs';
import { DatalayerService } from '@analytics/datalayer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Service responsible for tracking navigation events and forwarding them to analytics.
 * Uses Angular's Router events to detect navigation changes and sends page view data
 * to the data layer for analytics tracking.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationEventsService {
  /** Router instance for listening to navigation events */
  readonly #router = inject(Router);

  /** Service for pushing analytics events to the data layer */
  readonly #datalayer = inject(DatalayerService);

  /**
   * Observable pipeline that processes navigation events:
   * 1. Filters for NavigationEnd events
   * 2. Extracts the final URL after any redirects
   * 3. Pushes page view events to the data layer
   * 4. Automatically unsubscribes when the service is destroyed
   */
  readonly #navigationEventsForwarder = this.#router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map((e: NavigationEnd) => {
      return e.urlAfterRedirects;
    }),
    tap((url) => this.#datalayer.pushPageView(url)),
    takeUntilDestroyed()
  );

  /**
   * Initiates tracking of navigation events by subscribing to the events pipeline.
   * Should be called once during application initialization.
   */
  trackNavigationEvents() {
    this.#navigationEventsForwarder.subscribe();
  }
}
